import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { MapPin, Calendar, User, FileText, Edit, Briefcase, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';
import { useLanguage } from '@/LanguageContext';
import { getTranslation } from '@/translations';

const JobDetails = ({ job, onClose, onEdit, onUpdate }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'No date set';
    const [year, month, day] = dateString.split('-');
    return `${month}-${day}-${year}`;
  };
  const { language } = useLanguage();
  const t = (key) => getTranslation(key, language);
  
  const [deleting, setDeleting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const userRole = localStorage.getItem('role');
  const username = localStorage.getItem('username');
  const isOwner = userRole === 'owner';
  const canDelete = isOwner || job.created_by === username;

  const handleDelete = async () => {
    setDeleting(true);
    try {
      console.log('Deleting job:', job.id);
      const response = await axiosInstance.delete(`/jobs/${job.id}`);
      console.log('Delete response:', response);
      toast.success(t('job_deleted'));
      setShowDeleteDialog(false);
      onClose();
      onUpdate();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete job');
      setDeleting(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto dark:bg-slate-800 dark:text-slate-200" data-testid="job-details-dialog" aria-describedby="job-details-description">
        <DialogHeader className="pr-12">
          <div className="flex justify-between items-start gap-4">
            <DialogTitle className="text-2xl font-bold text-slate-800 dark:text-slate-100">
              {t('job_details')}
            </DialogTitle>
            <div className="flex gap-2">
              <Button
                data-testid="edit-job-button"
                onClick={() => {
                  onClose();
                  onEdit(job);
                }}
                className="bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700"
                size="sm"
              >
                <Edit className="w-4 h-4 mr-2" />
                {t('edit')}
              </Button>
              {canDelete && (
                <Button
                  data-testid="delete-job-button"
                  onClick={() => setShowDeleteDialog(true)}
                  disabled={deleting}
                  variant="destructive"
                  className="bg-red-500 hover:bg-red-600"
                  size="sm"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  {t('delete')}
                </Button>
              )}
            </div>
          </div>
        </DialogHeader>
        <p id="job-details-description" className="sr-only">
          View complete job information including photos, client details, address, and work description.
        </p>

        <div className="space-y-6 mt-4">
          {/* Photos Gallery */}
          {job.photos && job.photos.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-slate-800">Photos</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {job.photos.map((photo, index) => (
                  <div
                    key={index}
                    className="aspect-square rounded-lg overflow-hidden border-2 border-slate-200 hover:border-sky-400 transition-colors"
                    data-testid={`job-photo-${index}`}
                  >
                    <img
                      src={photo}
                      alt={`Job photo ${index + 1}`}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Job Information */}
          <div className="space-y-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg p-6">
            <div className="space-y-3">
              <div>
                <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">Client Name</h3>
                <p className="text-lg font-semibold text-slate-800 dark:text-slate-100" data-testid="job-client-name">{job.client_name}</p>
              </div>

              <div className="flex items-start space-x-2">
                <MapPin className="w-5 h-5 text-sky-500 mt-1" />
                <div>
                  <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">Address</h3>
                  <p className="text-slate-700 dark:text-slate-300" data-testid="job-address">{job.address}</p>
                </div>
              </div>

              <div className="flex items-start space-x-2">
                <Calendar className="w-5 h-5 text-blue-500 mt-1" />
                <div>
                  <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">Date</h3>
                  <p className="text-slate-700 dark:text-slate-300" data-testid="job-date">{formatDate(job.date)}</p>
                </div>
              </div>

              <div className="flex items-start space-x-2">
                <User className="w-5 h-5 text-purple-500 mt-1" />
                <div>
                  <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">Main Technician</h3>
                  <p className="text-slate-700 dark:text-slate-300" data-testid="job-technician">{job.main_technician}</p>
                </div>
              </div>

              {job.created_by && (
                <div className="flex items-start space-x-2">
                  <Briefcase className="w-5 h-5 text-indigo-500 mt-1" />
                  <div>
                    <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">{t('created_by')}</h3>
                    <p className="text-slate-700 dark:text-slate-300 font-medium" data-testid="job-created-by">{job.created_by}</p>
                  </div>
                </div>
              )}

              {job.price && (
                <div className="flex items-start space-x-2">
                  <span className="text-2xl mt-1">💰</span>
                  <div>
                    <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">{t('price')}</h3>
                    <p className="text-slate-700 dark:text-slate-300 font-bold text-lg" data-testid="job-price">${parseFloat(job.price).toFixed(2)}</p>
                  </div>
                </div>
              )}

              <div className="flex items-start space-x-2">
                <span className={`text-2xl mt-1 ${job.status === 'completed' ? '✅' : '⏳'}`}>
                  {job.status === 'completed' ? '✅' : '⏳'}
                </span>
                <div>
                  <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">{t('status')}</h3>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    job.status === 'completed' 
                      ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' 
                      : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
                  }`}>
                    {job.status === 'completed' ? t('completed') : t('pending')}
                  </span>
                </div>
              </div>

              <div className="flex items-start space-x-2">
                <span className="text-2xl mt-1">📄</span>
                <div>
                  <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">{t('invoice_sent')}</h3>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    job.invoice_sent 
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' 
                      : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                  }`}>
                    {job.invoice_sent ? t('invoice_sent') : 'Not Sent'}
                  </span>
                </div>
              </div>

              <div className="flex items-start space-x-2">
                <FileText className="w-5 h-5 text-green-500 mt-1" />
                <div>
                  <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">Description</h3>
                  <p className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap" data-testid="job-description">{job.description}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Metadata */}
          <div className="text-xs text-slate-400 space-y-1 border-t pt-4">
            <p>Created: {new Date(job.created_at).toLocaleString()}</p>
            <p>Last Updated: {new Date(job.updated_at).toLocaleString()}</p>
          </div>
        </div>
      </DialogContent>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('delete_job_title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('delete_confirmation')} <strong>{job.client_name}</strong>? 
              {t('delete_warning')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-500 hover:bg-red-600"
            >
              {deleting ? 'Deleting...' : t('delete_job_button')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
};

export default JobDetails;
